import React from 'react'
import { View, TouchableOpacity, StyleSheet } from 'react-native'
import { Image } from 'expo-image'
import { useTheme } from '@react-navigation/native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Theme } from '@theme'
import { Text } from '../atoms'
import { Skeleton } from './skeleton'
import { MaterialIcons as Icon } from '@expo/vector-icons'
import { useGetProfile } from '@hooks'
import { initialsOf } from '@utils'
import { useLanguage } from '../../context/LanguageContext'

interface ProfileHeaderProps {
  onProfilePress: () => void
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ onProfilePress }) => {
  const theme = useTheme()
  const { top } = useSafeAreaInsets()
  const { data: profile, isLoading } = useGetProfile()
  const { t } = useLanguage()
  const styles = getStyle(theme, top)

  return (
    <View style={styles.container}>
      <View style={styles.profileInfo}>
        {isLoading ? (
          <Skeleton width={80} height={80} borderRadius={40} style={styles.avatarSkeleton} />
        ) : profile?.avatarUrl ? (
          <Image
            source={{ uri: profile.avatarUrl }}
            style={styles.avatar}
            cachePolicy="memory-disk"
            transition={200}
            contentFit="cover"
          />
        ) : (
          // No photo: the person's initials, never a stock photo of someone else.
          <View style={[styles.avatar, styles.initials]} testID="profile-header-initials">
            <Text type="titleBold" color={theme.colors.primary}>
              {initialsOf(profile?.firstName, profile?.lastName)}
            </Text>
          </View>
        )}
        {isLoading ? (
          <Skeleton width={160} height={24} borderRadius={4} style={styles.textSkeleton} />
        ) : (
          <Text type="titleBold" style={styles.userName}>{profile?.fullName}</Text>
        )}
        {isLoading ? (
          <Skeleton width={100} height={14} borderRadius={4} />
        ) : (
          <TouchableOpacity onPress={onProfilePress}>
            <Text type="bodySmall" style={styles.viewProfile}>{t('ghostProfile.viewMyProfile')}</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  )
}

const getStyle = (theme: Theme, top: number) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.primary,
      paddingTop: top,
      paddingBottom: 30,
      paddingHorizontal: 20,
    },
    statusBar: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
    },
    time: {
      color: 'white',
      fontSize: 16,
      fontWeight: '600',
    },
    statusIcons: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    icon: {
      marginLeft: 4,
    },
    profileInfo: {
      alignItems: 'flex-start',
    },
    avatar: {
      width: 80,
      height: 80,
      borderRadius: 40,
      marginBottom: 15,
      borderWidth: 3,
      borderColor: 'white',
    },
    userName: {
      color: 'white',
      fontSize: 24,
      fontWeight: '600',
      marginBottom: 5,
    },
    viewProfile: {
      color: 'rgba(255, 255, 255, 0.8)',
      fontSize: 14,
      textDecorationLine: 'underline',
    },
    initials: {
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.white,
    },
    avatarSkeleton: {
      marginBottom: 15,
    },
    textSkeleton: {
      marginBottom: 5,
    },
  }) 